import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, Button, Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

const SKELETON_BG = "rgba(0,0,0,0.06)";

export interface DataTableWrapperProps {
  /** Show skeleton rows instead of children */
  loading?: boolean;
  /** Show error state instead of table */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Callback when user clicks retry (optional) */
  onRetry?: () => void;
  /** Table content: pass <Table><TableHead>...</TableHead><TableBody>...</TableBody></Table>; use stickyHeader on Table for sticky header */
  children: React.ReactNode;
  /** Min width for the table (used in skeleton and content wrapper) */
  minWidth?: number;
  /** Optional sx for Paper */
  paperSx?: SxProps<Theme>;
}

/**
 * Wraps a table in Paper and handles loading (skeleton rows) and error (message + optional retry).
 * Parent still controls empty state (EmptyState component).
 */
export default function DataTableWrapper({
  loading = false,
  error = false,
  errorMessage = "Failed to load data.",
  onRetry,
  children,
  minWidth = 600,
  paperSx,
}: DataTableWrapperProps) {
  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center", ...paperSx }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
        {onRetry && (
          <Button variant="outlined" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ overflow: "hidden", width: "100%", ...paperSx }}>
        <Table sx={{ minWidth }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: SKELETON_BG }} />
              <TableCell sx={{ bgcolor: SKELETON_BG }} />
              <TableCell sx={{ bgcolor: SKELETON_BG }} />
              <TableCell sx={{ bgcolor: SKELETON_BG }} />
              <TableCell sx={{ bgcolor: SKELETON_BG }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Box sx={{ height: 20, bgcolor: SKELETON_BG, borderRadius: 1, maxWidth: 120 }} />
                </TableCell>
                <TableCell>
                  <Box sx={{ height: 20, bgcolor: SKELETON_BG, borderRadius: 1, maxWidth: 80 }} />
                </TableCell>
                <TableCell>
                  <Box sx={{ height: 20, bgcolor: SKELETON_BG, borderRadius: 1, maxWidth: 160 }} />
                </TableCell>
                <TableCell>
                  <Box sx={{ height: 20, bgcolor: SKELETON_BG, borderRadius: 1, maxWidth: 100 }} />
                </TableCell>
                <TableCell>
                  <Box sx={{ height: 20, bgcolor: SKELETON_BG, borderRadius: 1, maxWidth: 60 }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflow: "auto", width: "100%", ...paperSx }}>
      <Box sx={{ minWidth }}>{children}</Box>
    </Paper>
  );
}
